<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';

const POSTPONE_DURATION = 60 * 60 * 1000;
const POSTPONED_UNTIL_KEY = 'yeszap-conversation-onboarding-postponed-until';
const HIGHLIGHT_CLASSES = [
  'relative',
  'z-[105]',
  'ring-2',
  'ring-inset',
  'ring-n-brand',
];

const { t } = useI18n();
const route = useRoute();
const { accountId, currentAccount, updateAccount } = useAccount();
const { isAdmin } = useAdmin();

const currentStepIndex = ref(0);
const targetAvailable = ref(false);
const postponedUntil = ref(0);
const isCompleting = ref(false);
let highlightedElement = null;
let postponeTimer = null;

const steps = computed(() => [
  {
    selector: '[data-onboarding="conversation-panel"]',
    icon: 'i-lucide-messages-square',
    title: t('CONVERSATION_ONBOARDING.STEPS.OVERVIEW.TITLE'),
    description: t('CONVERSATION_ONBOARDING.STEPS.OVERVIEW.DESCRIPTION'),
  },
  {
    selector: '[data-onboarding="conversation-tabs"]',
    icon: 'i-lucide-list-filter',
    title: t('CONVERSATION_ONBOARDING.STEPS.TABS.TITLE'),
    description: t('CONVERSATION_ONBOARDING.STEPS.TABS.DESCRIPTION'),
  },
  {
    selector: '[data-onboarding="new-conversation"]',
    icon: 'i-lucide-message-square-plus',
    title: t('CONVERSATION_ONBOARDING.STEPS.NEW_MESSAGE.TITLE'),
    description: t('CONVERSATION_ONBOARDING.STEPS.NEW_MESSAGE.DESCRIPTION'),
  },
  {
    selector: '[data-onboarding="conversation-list"]',
    icon: 'i-lucide-message-circle-reply',
    title: t('CONVERSATION_ONBOARDING.STEPS.LIST.TITLE'),
    description: t('CONVERSATION_ONBOARDING.STEPS.LIST.DESCRIPTION'),
  },
]);

const onboardingStatus = computed(
  () => currentAccount.value?.custom_attributes?.yeszap_onboarding_status
);
const currentStep = computed(() => steps.value[currentStepIndex.value]);
const isLastStep = computed(
  () => currentStepIndex.value === steps.value.length - 1
);
const nextButtonLabel = computed(() =>
  isLastStep.value
    ? t('CONVERSATION_ONBOARDING.FINISH')
    : t('CONVERSATION_ONBOARDING.NEXT')
);
const isPostponed = computed(() => postponedUntil.value > Date.now());
const shouldShow = computed(
  () =>
    isAdmin.value &&
    onboardingStatus.value === 'conversations_pending' &&
    !isPostponed.value &&
    targetAvailable.value
);

const postponementStorageKey = () =>
  `${POSTPONED_UNTIL_KEY}-${accountId.value}`;

const clearHighlight = () => {
  if (!highlightedElement) return;

  highlightedElement.classList.remove(...HIGHLIGHT_CLASSES);
  highlightedElement = null;
};

const syncTarget = async () => {
  await nextTick();
  clearHighlight();
  targetAvailable.value = false;

  if (
    !isAdmin.value ||
    onboardingStatus.value !== 'conversations_pending' ||
    isPostponed.value
  ) {
    return;
  }

  highlightedElement = document.querySelector(currentStep.value.selector);
  if (!highlightedElement) return;

  highlightedElement.classList.add(...HIGHLIGHT_CLASSES);
  highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  targetAvailable.value = true;
};

const clearPostponeTimer = () => {
  if (!postponeTimer) return;

  window.clearTimeout(postponeTimer);
  postponeTimer = null;
};

const scheduleTourReturn = () => {
  clearPostponeTimer();
  const remainingTime = postponedUntil.value - Date.now();

  if (remainingTime <= 0) {
    postponedUntil.value = 0;
    window.localStorage.removeItem(postponementStorageKey());
    syncTarget();
    return;
  }

  postponeTimer = window.setTimeout(() => {
    postponedUntil.value = 0;
    window.localStorage.removeItem(postponementStorageKey());
    postponeTimer = null;
    syncTarget();
  }, remainingTime);
};

const loadPostponement = () => {
  postponedUntil.value = Number(
    window.localStorage.getItem(postponementStorageKey()) || 0
  );
  scheduleTourReturn();
};

const postpone = () => {
  postponedUntil.value = Date.now() + POSTPONE_DURATION;
  window.localStorage.setItem(
    postponementStorageKey(),
    String(postponedUntil.value)
  );
  clearHighlight();
  targetAvailable.value = false;
  scheduleTourReturn();
};

const previousStep = () => {
  if (currentStepIndex.value > 0) currentStepIndex.value -= 1;
};

const completeTour = async () => {
  if (isCompleting.value) return;

  isCompleting.value = true;
  try {
    await updateAccount(
      { yeszap_onboarding_status: 'completed' },
      { silent: true }
    );
    window.localStorage.removeItem(postponementStorageKey());
    clearHighlight();
    targetAvailable.value = false;
  } catch {
    useAlert(t('CONVERSATION_ONBOARDING.ERROR'));
  } finally {
    isCompleting.value = false;
  }
};

const nextStep = () => {
  if (isLastStep.value) {
    completeTour();
    return;
  }

  currentStepIndex.value += 1;
};

watch(currentStepIndex, syncTarget);
watch(() => route.fullPath, syncTarget);
watch(onboardingStatus, syncTarget);
watch(accountId, () => {
  currentStepIndex.value = 0;
  loadPostponement();
  syncTarget();
});

onMounted(() => {
  loadPostponement();
  syncTarget();
});

onBeforeUnmount(() => {
  clearHighlight();
  clearPostponeTimer();
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="shouldShow"
      class="fixed inset-0 z-[100] bg-black/60"
      aria-hidden="true"
    />
    <section
      v-if="shouldShow"
      class="fixed bottom-4 right-4 z-[110] flex w-[calc(100%-2rem)] max-w-sm flex-col overflow-hidden rounded-lg border border-n-weak bg-n-solid-2 shadow-2xl"
      role="dialog"
      aria-modal="true"
      :aria-label="currentStep.title"
    >
      <header class="flex items-start gap-3 p-5">
        <div
          class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-n-brand/15"
        >
          <Icon :icon="currentStep.icon" class="size-5 text-n-brand" />
        </div>
        <div class="min-w-0 flex-1">
          <p class="text-xs font-medium uppercase text-n-brand">
            {{ $t('CONVERSATION_ONBOARDING.EYEBROW') }}
          </p>
          <h2 class="mt-1 text-base font-semibold text-n-slate-12">
            {{ currentStep.title }}
          </h2>
          <p class="mt-2 text-sm leading-6 text-n-slate-11">
            {{ currentStep.description }}
          </p>
        </div>
        <NextButton
          ghost
          slate
          sm
          icon="i-lucide-x"
          :title="$t('CONVERSATION_ONBOARDING.POSTPONE')"
          @click="postpone"
        />
      </header>

      <footer
        class="flex flex-wrap items-center justify-between gap-3 border-t border-n-weak bg-n-alpha-1 p-4"
      >
        <span class="text-xs font-medium text-n-slate-10">
          {{
            $t('CONVERSATION_ONBOARDING.PROGRESS', {
              current: currentStepIndex + 1,
              total: steps.length,
            })
          }}
        </span>
        <div class="flex items-center gap-2">
          <NextButton
            v-if="currentStepIndex > 0"
            ghost
            slate
            sm
            :label="$t('CONVERSATION_ONBOARDING.BACK')"
            @click="previousStep"
          />
          <NextButton
            outline
            slate
            sm
            :label="$t('CONVERSATION_ONBOARDING.POSTPONE')"
            @click="postpone"
          />
          <NextButton
            solid
            blue
            sm
            :loading="isCompleting"
            :label="nextButtonLabel"
            @click="nextStep"
          />
        </div>
      </footer>
    </section>
  </Teleport>
</template>
